import { getGameRuntimeConfig } from "../api/config";
import { RUNTIME_ENDPOINTS_CHANGE_EVENT } from "../api/runtime-endpoints";
import { resolveGamePortalBaseUrl } from "../portal-origin";

export const GAME_EMBED_CHANNEL = "ggmnq:game-embed:v1";
export const GAME_EMBED_NONCE_QUERY_KEY = "game_embed";
export const GAME_EMBED_PACKAGE_QUERY_KEY = "package_id";
export const PORTAL_AUTH_TOKEN_STORAGE_KEY = "ai-playground-auth-token";

const embedNoncePattern = /^[A-Za-z0-9_-]{20,160}$/;
const allowedPortalTargets = new Set<EmbeddedPortalTarget>(["auth", "forum", "models", "membership", "recharge"]);

export type EmbeddedPortalTarget = "auth" | "forum" | "models" | "membership" | "recharge";

export interface EmbeddedPortalRequest {
  target: EmbeddedPortalTarget;
  selectedModelId?: number;
}

export interface EmbeddedGameSession {
  authenticated: boolean;
  token?: string;
  user?: unknown;
  revision: number;
}

type GameEmbedContext = {
  nonce: string;
  packageId: number;
  portalOrigin: string;
};

type GameReadyMessage = {
  channel: typeof GAME_EMBED_CHANNEL;
  type: "GAME_READY";
  nonce: string;
  packageId: number;
};

type OpenPortalMessage = {
  channel: typeof GAME_EMBED_CHANNEL;
  type: "OPEN_PORTAL";
  nonce: string;
  packageId: number;
  target: EmbeddedPortalTarget;
  selectedModelId?: number;
};

type SessionSyncMessage = {
  channel: typeof GAME_EMBED_CHANNEL;
  type: "SESSION_SYNC";
  nonce: string;
  packageId: number;
  revision: number;
  authenticated: boolean;
  token?: string;
  user?: unknown;
};

export type GameEmbedChildMessage = GameReadyMessage | OpenPortalMessage;
export type GameEmbedParentMessage = SessionSyncMessage;

export function readSameOriginPortalSession() {
  if (typeof window === "undefined") return { available: false, token: "" };

  try {
    const portalOrigin = new URL(
      resolveGamePortalBaseUrl(getGameRuntimeConfig().forumBaseUrl),
    ).origin;
    if (window.location.origin !== portalOrigin) return { available: false, token: "" };
    return {
      available: true,
      token: window.localStorage.getItem(PORTAL_AUTH_TOKEN_STORAGE_KEY)?.trim() ?? ""
    };
  } catch {
    // 浏览器禁用本地存储或运行时尚未配置时，不把未知状态误判为官网退出登录。
    return { available: false, token: "" };
  }
}

export function requestEmbeddedGamePortal(options: EmbeddedPortalRequest) {
  const context = readGameEmbedContext();
  if (!context || !allowedPortalTargets.has(options.target)) return false;

  const message: OpenPortalMessage = {
    channel: GAME_EMBED_CHANNEL,
    type: "OPEN_PORTAL",
    nonce: context.nonce,
    packageId: context.packageId,
    target: options.target
  };
  if (options.selectedModelId && options.selectedModelId > 0) {
    message.selectedModelId = options.selectedModelId;
  }
  window.parent.postMessage(message, context.portalOrigin);
  return true;
}

export function readEmbeddedGamePortalRequestContext() {
  const context = readGameEmbedContext();
  if (!context) return undefined;
  // iframe nonce 同时作为本次官网往返的一次性 state，父子页面无需再维护第二套随机值。
  return { state: context.nonce, packageId: context.packageId };
}

export function subscribeEmbeddedGameSession(onSession: (session: EmbeddedGameSession) => void) {
  if (typeof window === "undefined") return () => undefined;
  let context = readGameEmbedContext();
  let lastRevision = 0;

  const handleMessage = (event: MessageEvent<unknown>) => {
    if (!context || event.source !== window.parent || event.origin !== context.portalOrigin) return;
    const message = parseSessionSyncMessage(event.data, context);
    if (!message || message.revision <= lastRevision) return;
    lastRevision = message.revision;
    onSession({
      authenticated: message.authenticated,
      token: message.authenticated ? message.token : undefined,
      user: message.authenticated ? message.user : undefined,
      revision: message.revision
    });
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== PORTAL_AUTH_TOKEN_STORAGE_KEY) return;
    const sameOrigin = readSameOriginPortalSession();
    if (!sameOrigin.available) return;
    lastRevision += 1;
    onSession({
      authenticated: Boolean(sameOrigin.token),
      token: sameOrigin.token || undefined,
      revision: lastRevision
    });
  };

  const announceReady = () => {
    context = readGameEmbedContext();
    if (!context) return;
    const ready: GameReadyMessage = {
      channel: GAME_EMBED_CHANNEL,
      type: "GAME_READY",
      nonce: context.nonce,
      packageId: context.packageId
    };
    window.parent.postMessage(ready, context.portalOrigin);
  };

  window.addEventListener("message", handleMessage);
  window.addEventListener("storage", handleStorage);
  window.addEventListener(RUNTIME_ENDPOINTS_CHANGE_EVENT, announceReady);
  announceReady();

  return () => {
    window.removeEventListener("message", handleMessage);
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(RUNTIME_ENDPOINTS_CHANGE_EVENT, announceReady);
  };
}

function readGameEmbedContext(): GameEmbedContext | undefined {
  if (typeof window === "undefined" || window.parent === window) return undefined;

  try {
    const config = getGameRuntimeConfig();
    const params = new URL(window.location.href).searchParams;
    const nonce = params.get(GAME_EMBED_NONCE_QUERY_KEY)?.trim() ?? "";
    const packageId = Number(params.get(GAME_EMBED_PACKAGE_QUERY_KEY));
    if (!embedNoncePattern.test(nonce) || packageId !== config.packageId) return undefined;
    return {
      nonce,
      packageId,
      // iframe 会话必须信任实际承载它的官网，而不是 API 选举得到的其他站点。
      portalOrigin: new URL(resolveGamePortalBaseUrl(config.forumBaseUrl)).origin
    };
  } catch {
    return undefined;
  }
}

function parseSessionSyncMessage(data: unknown, context: GameEmbedContext): SessionSyncMessage | undefined {
  if (!data || typeof data !== "object") return undefined;
  const message = data as Partial<SessionSyncMessage>;
  if (
    message.channel !== GAME_EMBED_CHANNEL ||
    message.type !== "SESSION_SYNC" ||
    message.nonce !== context.nonce ||
    message.packageId !== context.packageId ||
    !Number.isSafeInteger(message.revision) ||
    (message.revision ?? 0) <= 0 ||
    typeof message.authenticated !== "boolean"
  )
    return undefined;

  if (message.authenticated) {
    const token = typeof message.token === "string" ? message.token.trim() : "";
    if (!token || token.length > 8192) return undefined;
  }
  return message as SessionSyncMessage;
}
